import React, { FunctionComponent } from 'react'
import { useTranslation } from 'next-i18next'
import { Tag, Text, usePercent } from '@afterpaytouch/core'
import { useCheckoutSizes } from '../../utils/responsive'
import { useAppRouter, useSUPFinanceFee, useIsAfterpayFinanceFeeEligible, useCalculatedPaymentSchedules, useAmountDueToday } from '../../state'
import type { PaymentOption } from '../../model'
import styles from './style.module.css'
import { useMoney } from '../Money/useMoney'
import { PaymentType } from '@afterpay/types'

interface Props {
  paymentOption: PaymentOption
  selected?: boolean
}

type FormatMoneyFn = (input?: string | null | undefined) => string
type FormatPercentFn = (input?: string | number | null | undefined) => string

const InstallmentAmount: FunctionComponent<{ formatMoney: FormatMoneyFn; paymentOption: PaymentOption }> = ({ formatMoney, paymentOption }): JSX.Element => {
  const { textSize } = useCheckoutSizes()
  const pbiPaymentSchedules = useCalculatedPaymentSchedules()
  const installmentAmount =
    paymentOption.paymentType === PaymentType.PBI ? formatMoney(pbiPaymentSchedules[0].amount.amount) : formatMoney(paymentOption.installmentAmount.amount)
  const isAFFPBI = useIsAfterpayFinanceFeeEligible() && paymentOption.paymentType === PaymentType.PBI
  const { t } = useTranslation()
  return (
    <Text size={textSize} bold testNameSpace='installment-amount'>
      {t('consumerLending:paymentOptionLabel:installmentAmount', { context: `${paymentOption.paymentInterval}${isAFFPBI ? '_aff' : ''}`, installmentAmount })}
    </Text>
  )
}

const DownPaymentAmount: FunctionComponent<{ formatMoney: FormatMoneyFn; paymentOption: PaymentOption }> = ({ formatMoney, paymentOption }): JSX.Element => {
  const pbiAmountDueToday = useAmountDueToday()
  const downPaymentAmount =
    paymentOption.paymentType === PaymentType.PBI ? formatMoney(pbiAmountDueToday?.amount) : formatMoney(paymentOption.amountDueToday.amount)
  const { smallTextSize } = useCheckoutSizes()
  const { t } = useTranslation()
  return (
    <div className={styles.downPaymentText}>
      <Text size={smallTextSize} color='Subtle' testNameSpace='down-payment-amount'>
        {t('consumerLending:paymentOptionLabel:downPaymentAmount', { downPaymentAmount })}
      </Text>
    </div>
  )
}

const InterestAmount: FunctionComponent<{
  formatMoney: FormatMoneyFn
  paymentOption: PaymentOption
  formatPercentZero: FormatPercentFn
  formatPercent: FormatPercentFn
}> = ({ formatMoney, paymentOption, formatPercentZero, formatPercent }): JSX.Element => {
  const { t } = useTranslation()
  const { smallTextSize } = useCheckoutSizes()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const supFinanceFee = useSUPFinanceFee()
  const financeFee = formatMoney(supFinanceFee?.variableAmount.amount)
  const interestAmount = paymentOption.interestAmount === null ? formatMoney('0.00') : formatMoney(paymentOption.interestAmount.amount)
  const apr = !paymentOption.apr ? formatPercentZero(paymentOption.apr) : formatPercent(paymentOption.apr)
  const isAffPbi = paymentOption.paymentType === PaymentType.PBI && isAFF
  const i18nKey = isAffPbi ? 'consumerLending:paymentOptionLabel:financeFeeAmount' : 'consumerLending:paymentOptionLabel:interestAmount'
  const params = isAffPbi ? { financeFee } : { interestAmount, apr, context: isAFF ? 'aff' : '' }
  return (
    <div className={styles.interestText}>
      <Text size={smallTextSize} color='Subtle' testNameSpace='interest-amount'>
        {t(i18nKey, params)}
      </Text>
    </div>
  )
}
export const PaymentOptionLabel: FunctionComponent<Props> = ({ paymentOption, selected = false }) => {
  const { t } = useTranslation()
  const { locale } = useAppRouter()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })
  const { formatPercent: formatPercentZero } = usePercent({ locale, decimalPlaces: 0, shouldDivideBy100: true })

  const { formatMoney } = useMoney({
    locale,
    currency: paymentOption.installmentAmount.currency,
  })

  return (
    <>
      <div className={styles.paymentsCountTag}>
        <Tag kind={selected ? 'Mint' : 'Gray'}>
          {t('consumerLending:paymentsCount', { count: paymentOption.installmentsCount, context: paymentOption.paymentInterval })}
        </Tag>
      </div>
      <InstallmentAmount formatMoney={formatMoney} paymentOption={paymentOption} />
      <DownPaymentAmount formatMoney={formatMoney} paymentOption={paymentOption} />
      <InterestAmount formatMoney={formatMoney} formatPercent={formatPercent} formatPercentZero={formatPercentZero} paymentOption={paymentOption} />
    </>
  )
}
